import React, { useState, useEffect } from 'react';
import { Client, SubscriptionPlan, UserAccount } from '../types';
import {
  ArrowLeftIcon,
  CloudIcon,
  MenuIcon,
  InfoIcon,
  LogoutIcon,
  StarIcon,
  DownloadIcon,
  UserIcon,
  ChartBarIcon,
} from './icons';
import { InstallBanner } from './InstallBanner';
import InstallModal from './InstallModal';
import type { MobileTab } from './mobile/BottomNav';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface AppHeaderProps {
  currentUser: UserAccount | null;
  selectedClient: Client | null;
  activeTab: MobileTab;
  syncStatus: string;
  syncError: string | null;
  syncMeta: { lastSyncAt: string | null; lastAttemptAt: string | null };
  pendingUploads: Record<string, unknown>;
  effectiveSubscription: { subscription_status: string; storage_used_mb?: number; storage_quota_mb?: number };
  subscriptionPlan: SubscriptionPlan;
  activeClientCount: number;
  remainingClients: number;
  PLAN_LABELS: Record<string, string>;
  PLAN_CLIENT_LIMITS: Record<string, number>;
  actualSubscriptionStatus: string;
  safeUserId: string | null;
  canUpgradeEarly: boolean;
  ordersEnabled: boolean;
  showInstallCta: boolean;
  deferredPrompt: BeforeInstallPromptEvent | null;
  isInstallPromptAvailable: boolean;
  isStandalone: boolean;
  showInstallModal: boolean;
  setShowInstallModal: (v: boolean) => void;
  triggerPwaInstall: () => void;
  onSetActiveTab: (tab: MobileTab) => void;
  onSetSelectedClient: (client: Client | null) => void;
  onLogout: () => void;
  onInstallClick: () => Promise<void>;
  onShowProfileSettings: () => void;
  onPerformSync: (opts: { manual: boolean }) => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  currentUser,
  selectedClient,
  activeTab,
  syncStatus,
  syncError,
  syncMeta,
  pendingUploads,
  effectiveSubscription,
  subscriptionPlan,
  activeClientCount,
  remainingClients,
  PLAN_LABELS,
  PLAN_CLIENT_LIMITS,
  actualSubscriptionStatus,
  safeUserId,
  canUpgradeEarly,
  ordersEnabled,
  showInstallCta,
  deferredPrompt,
  isInstallPromptAvailable,
  isStandalone,
  showInstallModal,
  setShowInstallModal,
  triggerPwaInstall,
  onSetActiveTab,
  onSetSelectedClient,
  onLogout,
  onInstallClick,
  onShowProfileSettings,
  onPerformSync,
}) => {
  const displayName =
    currentUser?.full_name?.trim() || currentUser?.phone || currentUser?.email || 'Mon espace';

  const planLabel =
    (effectiveSubscription?.subscription_status || 'free') === 'active'
      ? (() => {
          const pn = PLAN_LABELS?.[(subscriptionPlan ?? 'free')] || 'Gratuit';
          const mx = PLAN_CLIENT_LIMITS?.[(subscriptionPlan ?? 'free')] ?? 3;
          const ul = mx === -1;
          const rm = ul ? 0 : Math.max(0, mx - activeClientCount);
          return ul ? `Plan ${pn} • Clients illimités` : `Plan ${pn} • ${rm} clients restants`;
        })()
      : `Plan Gratuit • ${remainingClients} clients restants`;

  const lastSyncLabel = syncMeta.lastSyncAt
    ? `Sync: ${new Date(syncMeta.lastSyncAt).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })}`
    : 'Non synchronisé';

  const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
  const syncButtonDisabled = syncStatus === 'syncing' || !isOnline;
  const syncButtonText = syncStatus === 'syncing' ? 'Sync...' : 'Synchroniser';
  const syncStatusColor =
    syncStatus === 'error'
      ? 'text-red-400 dark:text-red-300'
      : syncStatus === 'success'
        ? 'text-emerald-500 dark:text-emerald-300'
        : 'text-indigo-600 dark:text-indigo-200';

  const [mobileMetaOpen, setMobileMetaOpen] = useState(false);

  useEffect(() => {
    setMobileMetaOpen(false);
  }, [selectedClient]);

  const getHeaderTitle = () => {
    if (selectedClient) return selectedClient.name;
    if (activeTab === 'dashboard') return 'Tableau de Bord';
    if (activeTab === 'about') return 'À Propos';
    switch (activeTab) {
      case 'gallery': return 'Galeries';
      case 'orders': return 'Commandes';
      case 'profile': return 'Mon Profil';
      default: return 'Mes Clients';
    }
  };

  // suppress unused warning
  void getHeaderTitle;

  return (
    <>
      {/* Install Banner - shown at top when app can be installed */}
      <InstallBanner
        deferredPrompt={deferredPrompt}
        isInstallPromptAvailable={isInstallPromptAvailable}
        isStandalone={isStandalone}
        onInstallClick={onInstallClick}
      />

      <InstallModal
        isOpen={showInstallModal}
        onClose={() => setShowInstallModal(false)}
        onInstallPwa={triggerPwaInstall}
        isInstallPromptAvailable={isInstallPromptAvailable}
      />

      <header className="sticky top-0 z-30 bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-700 shadow-lg shadow-indigo-500/20">
        {/* Décoration subtile */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent pointer-events-none" />

        <div className="relative mx-auto w-full max-w-6xl">
          {/* Alerte Quota Plein */}
          {effectiveSubscription?.storage_quota_mb && effectiveSubscription.storage_quota_mb > 0 && 
           (effectiveSubscription.storage_used_mb ?? 0) >= effectiveSubscription.storage_quota_mb && (
            <div className="bg-amber-500 text-white px-4 py-2 text-center text-xs font-bold animate-in fade-in slide-in-from-top-1 duration-500 shadow-inner">
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>Attention, votre sauvegarde cloud est pleine. Vos nouvelles photos ne sont plus protégées en ligne.</span>
              </span>
            </div>
          )}

          <div className="relative w-full px-4 py-3 md:px-6">
            <div className="flex items-center justify-between gap-3">
            {/* Bouton retour / Logo */}
            <div className="flex items-center gap-3">
              {selectedClient ? (
                <button
                  onClick={() => onSetSelectedClient(null)}
                  className="flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-3 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-white/25 hover:shadow-md"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">Retour</span>
                </button>
              ) : (
                <div
                  className="flex items-center gap-3 cursor-pointer group"
                  onClick={() => {
                    onSetSelectedClient(null);
                    onSetActiveTab('clients');
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-lg shadow-indigo-500/20 group-hover:scale-105 transition-transform active:scale-95 overflow-hidden">
                    <img src="logo.svg" alt="M" className="h-full w-full object-cover" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-lg font-black tracking-tight text-white leading-tight">
                      Carnet de Mesures
                    </span>
                    <span className="text-[10px] font-bold text-indigo-100 uppercase tracking-widest">
                      Atelier Numérique
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Info utilisateur - Desktop */}
            <div className="hidden md:flex items-center gap-4">
              {/* Statut sync */}
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm">
                <span className={`flex items-center gap-1.5 text-xs font-medium ${syncStatusColor}`}>
                  <CloudIcon />
                  {syncError ? 'Erreur sync' : syncStatus === 'syncing' ? 'Sync...' : lastSyncLabel}
                </span>
                {Object.keys(pendingUploads).length > 0 && (
                  <span className="ml-2 animate-pulse text-[10px] font-bold text-amber-300">
                    ↑ {Object.keys(pendingUploads).length} im.
                  </span>
                )}
                <button
                  onClick={() => onPerformSync({ manual: true })}
                  disabled={syncButtonDisabled}
                  className="ml-1 px-2 py-0.5 rounded-full bg-white/20 text-[10px] font-semibold text-white transition hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {syncButtonText}
                </button>
              </div>

              {/* Info utilisateur */}
              <div className="flex items-center gap-3 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white font-bold text-sm shadow-sm">
                  {displayName.charAt(0).toUpperCase()}
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-white leading-tight">{displayName}</span>
                  <span className="text-[10px] text-indigo-200">{planLabel}</span>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="flex items-center gap-2">
                {showInstallCta && (
                  <button
                    onClick={onInstallClick}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/15 text-sm font-medium text-white transition-all hover:bg-white/25"
                  >
                    <DownloadIcon />
                    <span>Installer</span>
                  </button>
                )}

                {/* Dashboard button - Pro/Premium only */}
                {ordersEnabled && (
                  <button
                    onClick={() => onSetActiveTab('dashboard')}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all ${
                      activeTab === 'dashboard' ? 'bg-white text-indigo-600 shadow-md' : 'bg-white/15 text-white hover:bg-white/25'
                    }`}
                    title="Tableau de bord"
                  >
                    <ChartBarIcon className="w-4 h-4" />
                    <span className="hidden sm:inline">Dashboard</span>
                  </button>
                )}

                <button
                  onClick={onShowProfileSettings}
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-white/10 text-white transition-all hover:bg-white/25"
                  title="Paramètres"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>

                <button
                  onClick={() => onSetActiveTab('about')}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all ${
                    activeTab === 'about' ? 'bg-white text-indigo-600 shadow-md' : 'bg-white/15 text-white hover:bg-white/25'
                  }`}
                >
                  <InfoIcon />
                  <span className="hidden sm:inline">À propos</span>
                </button>

                {canUpgradeEarly && safeUserId && (
                  <a
                    href="/abonnement/"
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-sm font-bold text-white shadow-lg shadow-amber-500/30 transition-all hover:shadow-amber-500/50 hover:scale-105"
                  >
                    <StarIcon />
                    <span>
                      {actualSubscriptionStatus === 'expired'
                        ? 'Renouveler'
                        : actualSubscriptionStatus === 'active'
                          ? 'Changer de plan'
                          : "S'abonner"}
                    </span>
                  </a>
                )}

                <button
                  onClick={onLogout}
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-white/10 text-white transition-all hover:bg-red-500/80 hover:shadow-lg"
                  title="Déconnexion"
                >
                  <LogoutIcon />
                </button>
              </div>
            </div>

            {/* Menu mobile */}
            <button
              type="button"
              onClick={() => setMobileMetaOpen((prev) => !prev)}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-white/15 text-white transition-all hover:bg-white/25 md:hidden"
              aria-label="Ouvrir le menu utilisateur"
            >
              <MenuIcon className="h-5 w-5" />
            </button>
            </div>
          </div>

          {/* Menu mobile déroulant */}
          {mobileMetaOpen && (
            <div className="mt-3 rounded-2xl bg-white dark:bg-slate-800 backdrop-blur-lg p-4 shadow-xl border border-white/50 dark:border-white/10 md:hidden animate-in slide-in-from-top-2 duration-200">
              {/* En-tête utilisateur */}
              <div className="flex items-center gap-3 pb-3 border-b border-gray-100 dark:border-white/5">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white font-bold text-lg shadow-md">
                  {displayName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold text-gray-800 dark:text-white">{displayName}</p>
                  <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">{planLabel}</p>
                </div>
              </div>

              {/* Statut sync */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-white/5">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${syncStatus === 'error' ? 'bg-red-500' : syncStatus === 'success' ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                  <span className="text-xs text-gray-600 dark:text-slate-400">
                    {syncError ? `Erreur: ${syncError}` : syncStatus === 'syncing' ? 'Synchronisation...' : lastSyncLabel}
                  </span>
                </div>
                {Object.keys(pendingUploads).length > 0 && (
                  <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-[10px] font-bold text-amber-700 dark:text-amber-400">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                    </span>
                    {Object.keys(pendingUploads).length} image(s) en attente
                  </div>
                )}
                <button
                  onClick={() => onPerformSync({ manual: true })}
                  disabled={syncButtonDisabled}
                  className="px-3 py-1.5 rounded-full bg-indigo-100 dark:bg-white/10 text-xs font-semibold text-indigo-700 dark:text-indigo-300 transition hover:bg-indigo-200 dark:hover:bg-white/20 disabled:opacity-50"
                >
                  {syncButtonText}
                </button>
              </div>

              {/* Actions */}
              <div className="grid gap-2 pt-3">
                {showInstallCta && (
                  <button
                    onClick={() => { onInstallClick(); setMobileMetaOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 text-gray-700 font-medium transition hover:bg-gray-100"
                  >
                    <DownloadIcon />
                    <span>Installer l'application</span>
                  </button>
                )}

                <button
                  onClick={() => {
                    onShowProfileSettings();
                    setMobileMetaOpen(false);
                  }}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 text-gray-700 dark:text-slate-300 font-medium transition hover:bg-gray-100 dark:hover:bg-white/10"
                >
                  <UserIcon className="w-5 h-5 text-indigo-500" />
                  <span>Mon Profil & Paramètres</span>
                </button>

                {ordersEnabled && (
                  <button
                    onClick={() => {
                      onSetActiveTab('dashboard');
                      setMobileMetaOpen(false);
                    }}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 text-gray-700 dark:text-slate-300 font-medium transition hover:bg-gray-100 dark:hover:bg-white/10"
                  >
                    <ChartBarIcon className="w-5 h-5 text-blue-500" />
                    <span>Tableau de Bord</span>
                  </button>
                )}

                <button
                  onClick={() => {
                    onSetActiveTab('about');
                    setMobileMetaOpen(false);
                  }}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 text-gray-700 dark:text-slate-300 font-medium transition hover:bg-gray-100 dark:hover:bg-white/10"
                >
                  <InfoIcon />
                  <span>À propos</span>
                </button>

                {canUpgradeEarly && safeUserId && (
                  <a
                    href="/abonnement/"
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold shadow-md transition hover:shadow-lg"
                  >
                    <StarIcon />
                    <span>Passer à Premium</span>
                  </a>
                )}

                <button
                  onClick={() => { setMobileMetaOpen(false); onLogout(); }}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-50 text-red-600 font-medium transition hover:bg-red-100"
                >
                  <LogoutIcon />
                  <span>Déconnexion</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default AppHeader;


